import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useHybridAuth } from './useHybridAuth';
import { useDriverProfile } from './useDriverProfile';
import { companyIdFromProfileOrStorage } from './userCompanies.shared';
import {
  ChecklistRow,
  InspectionSubjectInput,
  SubmitInspectionBody,
  setInspectionsContext,
  truckInspectionsApi,
} from '../api/truckInspections';

/**
 * The company these calls belong to, resolved the way the rest of the app
 * resolves it. The profile comes back without one for anybody who belongs to
 * more than one yard, and the stored selection is what covers that.
 */
const useInspectionsCompany = () => {
  const { data: profile } = useDriverProfile();
  return companyIdFromProfileOrStorage(profile) || undefined;
};

/** Every call needs a fresh token and the driver's company before it goes out. */
const useAuthedCall = () => {
  const { getAccessTokenSilently } = useHybridAuth();
  const company = useInspectionsCompany();
  return async <T>(run: () => Promise<T>): Promise<T> => {
    const token = await getAccessTokenSilently();
    setInspectionsContext(token, company);
    return run();
  };
};

export const useInspectionTrucks = (enabled = true) => {
  const call = useAuthedCall();
  // The company is in the key because these lists are per yard. Without it,
  // switching company showed the previous yard's trucks out of the cache.
  const company = useInspectionsCompany();
  return useQuery({
    queryKey: ['inspection-trucks', company],
    queryFn: () => call(() => truckInspectionsApi.listTrucks()),
    enabled,
    staleTime: 30_000,
  });
};

export const useDriverChecklists = (enabled = true) => {
  const call = useAuthedCall();
  const company = useInspectionsCompany();
  return useQuery({
    queryKey: ['inspection-checklists', company],
    queryFn: () => call(() => truckInspectionsApi.listChecklists()),
    enabled,
    staleTime: 30_000,
    /*
     * Always ask again on open, cached list on screen in the meantime.
     *
     * This list is where the run screen learns whether a checklist is written
     * for a truck, for equipment, or for either, and the office can change
     * that from a desk the driver never sees. Within the stale window the
     * phone was asking a driver holding a dolly to pick a truck, and the
     * server then refused the run.
     */
    refetchOnMount: 'always',
  });
};

export const useStartInspection = () => {
  const call = useAuthedCall();
  return useMutation({
    mutationFn: ({
      templateId,
      subject,
    }: {
      templateId: string;
      subject: InspectionSubjectInput;
    }) => call(() => truckInspectionsApi.start(templateId, subject)),
  });
};

export const useSubmitInspection = () => {
  const call = useAuthedCall();
  const company = useInspectionsCompany();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ body }: { body: SubmitInspectionBody }) =>
      call(() => truckInspectionsApi.submit(body)),
    /**
     * Write the run into the cache before the server is asked again.
     *
     * The app navigates back to the list the moment a report is sent, and the
     * list it lands on is the one it already had: the checklist still saying
     * it was last run yesterday, or not at all. On a slow link that stale line
     * sat there for seconds. The report that just succeeded says when this
     * checklist was run and on what, so say so, then confirm it in the
     * background.
     */
    onSuccess: (inspection, variables) => {
      const filed = inspection as {
        id?: string;
        submittedAt?: string | null;
        subjectLabel?: string | null;
      } | null;

      queryClient.setQueryData<ChecklistRow[]>(
        ['inspection-checklists', company],
        (current) =>
          current?.map((row) =>
            row.id === variables.body.templateId
              ? {
                  ...row,
                  lastRun: {
                    id: filed?.id ?? '',
                    submittedAt: filed?.submittedAt ?? new Date().toISOString(),
                    subjectLabel: filed?.subjectLabel ?? '',
                  },
                }
              : row
          )
      );

      queryClient.invalidateQueries({ queryKey: ['inspection-trucks'] });
      queryClient.invalidateQueries({ queryKey: ['inspection-checklists'] });
      queryClient.invalidateQueries({ queryKey: ['my-inspections'] });
    },
  });
};

/** One finished report. Shares a cache key with the report screen. */
export const useInspectionReport = (id: string, enabled = true) => {
  const { getAccessTokenSilently } = useHybridAuth();
  const company = useInspectionsCompany();
  return useQuery({
    queryKey: ['inspection-report', id, company],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return truckInspectionsApi.get(token, id, company);
    },
    enabled: enabled && Boolean(id),
  });
};

export const useMyInspections = (enabled = true) => {
  const call = useAuthedCall();
  const company = useInspectionsCompany();
  return useQuery({
    queryKey: ['my-inspections', company],
    queryFn: () => call(() => truckInspectionsApi.mine()),
    enabled,
  });
};

export const useUploadInspectionPhoto = () => {
  const call = useAuthedCall();
  return useMutation({
    mutationFn: (file: File) =>
      call(() => truckInspectionsApi.uploadPhoto(file)),
    // The photo outbox is the offline story here, not React Query. On the
    // default 'online' mode an offline upload is paused rather than run, the
    // promise never settles, and the outbox pass that is awaiting it never
    // ends, so every later attempt short-circuits and the photo sits Pending
    // until the screen is remounted. 'always' lets the request fail fast so
    // the outbox can keep it and try again in thirty seconds.
    networkMode: 'always',
  });
};
